// ============================================================================
// DÉCODAGE de l'enveloppe §16.2.7 — LA lecture, indépendante du runtime.
//
// ⚠️ POURQUOI CE FICHIER EST SÉPARÉ DE `client.ts` : le décodage est le CONTRAT ; le transport ne l'est
// pas. Un SPA parle en cookies de session + jeton CSRF (donc `document`), un service parle à un autre
// service en secret partagé + timeout — mais tous deux lisent EXACTEMENT la même forme de réponse.
// Tant que le décodeur vivait dans le client navigateur, aucun appel serveur→serveur ne pouvait le
// réutiliser : la Console avait donc DEUX lecteurs écrits à la main pour parler à l'IdP (audit
// 2026-08-02). Une forme, un lecteur ; les transports se branchent dessus.
//
// Zéro import de `fetch`, `document` ou Node : consommable partout.
// ============================================================================

import type { Envelope, EnvelopeError } from './index';

/** Erreur d'API : porte la CLÉ machine (i18n-able), un message affichable, le status HTTP et le détail champ. */
export class ApiError extends Error {
  constructor(
    /** Clé machine stable (`invalid_credentials`, `not_found`…) — sur quoi brancher une traduction. */
    readonly key: string,
    /** Texte AFFICHABLE (traduction de la clé si fournie, sinon `meta.detail`, sinon la clé). */
    message: string,
    /** Status HTTP réel. `0` = aucune réponse HTTP (panne réseau, DNS, timeout). */
    readonly status: number,
    /** Détail par champ (validation), vide si absent. */
    readonly errors: EnvelopeError[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Traduction d'une clé machine en texte affichable. À défaut : `meta.detail`, puis la clé elle-même. */
export type Humanize = (key: string, detail?: string) => string | undefined;

/**
 * Lit un corps de réponse enveloppé et rend `data`, ou LÈVE une `ApiError`.
 *
 * ⚠️ Une réponse HORS enveloppe est REFUSÉE explicitement plutôt que devinée : deviner masquerait une
 * régression serveur, et c'est exactement ce qui a rendu la connexion EDI impossible le 2026-08-01
 * (un décodeur maison lisait `data` à la racine et trouvait `undefined` sans rien signaler).
 */
export function decodeEnvelope<T>(status: number, text: string, humanize?: Humanize): T {
  let parsed: unknown = null;
  if (text) {
    // Corps non-JSON = page d'erreur d'un intermédiaire (502/504 nginx…) : message propre plutôt qu'un
    // « Unexpected token '<' » incompréhensible.
    try { parsed = JSON.parse(text); } catch {
      throw new ApiError('bad_response', 'Réponse illisible du serveur.', status);
    }
  }

  const env = parsed as Envelope<T> | null;
  if (!env || (env.status !== 'success' && env.status !== 'error')) {
    // ⚠️ LE STATUT HTTP FAIT PARTIE DU MOTIF, et il n'y était pas : « serveur non conforme » sur un
    //    501 désigne une route absente, sur un 201 une route qui a parfaitement travaillé avec la
    //    mauvaise FORME. Deux pannes opposées, deux conduites — et sans le chiffre, il faut aller
    //    lire les journaux du serveur pour les distinguer.
    throw new ApiError('bad_response', `Réponse hors enveloppe (§16.2.7) — serveur non conforme (HTTP ${status}).`, status);
  }
  if (env.status === 'error') {
    const detail = typeof env.meta?.detail === 'string' ? env.meta.detail : undefined;
    throw new ApiError(env.message, humanize?.(env.message, detail) ?? detail ?? env.message, env.code ?? status, env.errors ?? []);
  }
  return env.data as T;
}

/**
 * Panne de TRANSPORT (DNS, connexion refusée, timeout) : aucune réponse HTTP n'existe.
 * ⚠️ Normalisée en `ApiError` pour que l'appelant n'ait qu'UN type d'erreur à traiter — sinon chaque
 * site d'appel retombe sur un `TypeError: fetch failed` brut, illisible pour un opérateur.
 */
export const unreachable = (cause: unknown, what: string): ApiError =>
  new ApiError('unreachable', `${what} injoignable : ${(cause as Error)?.message ?? String(cause)}`, 0);
